'use client'

import { useState, useEffect, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import { popModal } from '@/components/modals'
import Image from 'next/image'
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  UserPlus,
  MailIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface ImportGmailModalProps {
  projectId: string
  onClose?: () => void
  onImportComplete?: () => void
  initialToken?: string | null
  initialError?: string | null
}

interface GmailContact {
  resourceName: string
  firstName: string
  lastName: string
  email: string
  phone: string
  address: string
}

export default function ImportGmailModal({
  projectId,
  onClose,
  onImportComplete,
  initialToken,
  initialError,
}: ImportGmailModalProps) {
  const t = useTranslations('customers.import_gmail')
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [contacts, setContacts] = useState<GmailContact[]>([])
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(
    new Set(),
  )
  const [error, setError] = useState<string | null>(null)
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false)

  // Check for initial token or error from props
  useEffect(() => {
    if (initialError) {
      if (initialError === 'denied') {
        setError(t('auth_denied'))
      } else if (initialError === 'auth_failed' || initialError === 'no_code') {
        setError(t('auth_failed'))
      } else {
        setError(initialError)
      }
    } else if (initialToken) {
      fetchContacts(initialToken)
    }
  }, [initialToken, initialError, t])

  /**
   * Fetches contacts from Gmail using access token
   */
  const fetchContacts = useCallback(
    async (accessToken: string) => {
      setIsFetching(true)
      setError(null)

      try {
        const response = await fetch('/api/import/google/contacts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken }),
        })

        const result = await response.json()

        if (result.success && result.data) {
          setContacts(result.data.contacts)
          // Auto-select all contacts initially
          setSelectedContacts(
            new Set(
              result.data.contacts.map((c: GmailContact) => c.resourceName),
            ),
          )
        } else {
          const errorMsg = result.message || t('fetch_failed')
          setError(errorMsg)
        }
      } catch (error) {
        setError(t('fetch_failed'))
      } finally {
        setIsFetching(false)
        setHasFetchedOnce(true)
      }
    },
    [t],
  )

  /**
   * Initiates OAuth flow to connect Gmail
   */
  const handleConnectGmail = () => {
    setIsLoading(true)
    // Add project ID to state parameter
    const authUrl = `/api/import/google/auth?state=${projectId}`
    window.location.href = authUrl
  }

  /**
   * Toggles contact selection
   */
  const toggleContactSelection = (resourceName: string) => {
    setSelectedContacts((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(resourceName)) {
        newSet.delete(resourceName)
      } else {
        newSet.add(resourceName)
      }
      return newSet
    })
  }

  /**
   * Toggles all contacts selection
   */
  const toggleAllContacts = () => {
    if (selectedContacts.size === contacts.length) {
      setSelectedContacts(new Set())
    } else {
      setSelectedContacts(new Set(contacts.map((c) => c.resourceName)))
    }
  }

  /**
   * Imports selected contacts
   */
  const handleImport = async () => {
    if (selectedContacts.size === 0) {
      toast.error(t('no_contacts_selected'))
      return
    }

    setIsImporting(true)
    setError(null)

    try {
      const contactsToImport = contacts.filter((c) =>
        selectedContacts.has(c.resourceName),
      )

      const apiUrl = `/api/me/project/${projectId}/customers/import-gmail`

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contacts: contactsToImport }),
      })

      const result = await response.json()

      if (result.success) {
        toast.success(t('import_success', { count: contactsToImport.length }))
        onImportComplete?.()
        popModal('ImportGmailModal')
      } else {
        const errorMsg = result.message || t('import_failed')
        setError(errorMsg)
      }
    } catch (error) {
      setError(t('import_failed'))
    } finally {
      setIsImporting(false)
    }
  }

  /**
   * Handles modal close
   */
  const handleClose = () => {
    if (!isLoading && !isImporting) {
      onClose?.()
      popModal('ImportGmailModal')
    }
  }

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0 rounded-lg border border-white/10 bg-popover"
        overlayClassName="z-[80] bg-black/20"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="flex items-center gap-3 text-xl font-semibold text-popover-foreground">
            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
              <Image
                src="/icons/gmail.svg"
                alt="Gmail"
                width={20}
                height={20}
              />
            </div>
            {t('title')}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground mt-2">
            {t('description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-6 py-4">
          {/* Error Alert */}
          {error && (
            <Alert
              variant="destructive"
              className="mb-4 border-red-500/20 bg-red-500/10"
            >
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-red-200">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Connect Gmail Button - Initial state */}
          {!contacts.length && !isFetching && !hasFetchedOnce && (
            <div className="flex flex-col items-center justify-center py-16 px-4 relative z-10">
              <div className="w-24 h-24 rounded-2xl bg-white/5 flex items-center justify-center mb-6 border border-white/10">
                <Image
                  src="/icons/gmail.svg"
                  alt="Gmail"
                  width={64}
                  height={64}
                  className="opacity-90"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-popover-foreground">
                {t('connect_title')}
              </h3>
              <p className="text-sm text-muted-foreground mb-8 text-center max-w-md leading-relaxed">
                {t('connect_description')}
              </p>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleConnectGmail()
                }}
                disabled={isLoading}
                className="gap-2 px-8 py-6 h-auto text-base font-medium rounded-xl bg-gold-gradient text-black font-bold shadow-[0_0_20px_-5px_rgba(255,215,0,0.4)] hover:shadow-[0_0_30px_-5px_rgba(255,215,0,0.6)] hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center relative z-20 pointer-events-auto"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="size-5 animate-spin" />
                    {t('connecting')}
                  </>
                ) : (
                  <>
                    <MailIcon className="size-5" />
                    {t('connect_button')}
                  </>
                )}
              </button>
            </div>
          )}

          {/* No Contacts Found - After successful fetch with 0 contacts */}
          {!contacts.length && !isFetching && hasFetchedOnce && !error && (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="rounded-full bg-muted/50 p-4 mb-6 border border-white/10">
                <AlertCircle className="size-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-popover-foreground">
                {t('no_contacts_title')}
              </h3>
              <p className="text-sm text-muted-foreground mb-8 text-center max-w-md leading-relaxed">
                {t('no_contacts_description')}
              </p>
              <Button
                onClick={handleConnectGmail}
                disabled={isLoading}
                size="lg"
                variant="outline"
                className="gap-2 px-8 py-6 h-auto text-base font-medium"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="size-5 animate-spin" />
                    {t('connecting')}
                  </>
                ) : (
                  <>
                    <MailIcon className="size-5" />
                    {t('try_again')}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Fetching Contacts */}
          {isFetching && (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <Loader2 className="size-10 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground">
                {t('fetching_contacts')}
              </p>
            </div>
          )}

          {/* Contacts Table */}
          {contacts.length > 0 && !isFetching && (
            <div>
              {/* Selection Summary */}
              <div className="flex items-center justify-between mb-6 p-4 bg-muted/30 rounded-lg border border-white/10">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedContacts.size === contacts.length}
                    onCheckedChange={toggleAllContacts}
                    className="border-white/20"
                  />
                  <span className="text-sm font-medium text-popover-foreground">
                    {t('select_all')}
                  </span>
                  <Badge
                    variant="secondary"
                    className="bg-white/10 text-popover-foreground border-white/20"
                  >
                    {selectedContacts.size} / {contacts.length}
                  </Badge>
                </div>
              </div>

              {/* Contacts Table */}
              <div className="border border-white/10 rounded-lg overflow-hidden bg-muted/20">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-white/10 hover:bg-transparent">
                      <TableHead className="w-12 text-muted-foreground font-medium" />
                      <TableHead className="text-muted-foreground font-medium">
                        {t('name')}
                      </TableHead>
                      <TableHead className="text-muted-foreground font-medium">
                        {t('email')}
                      </TableHead>
                      <TableHead className="text-muted-foreground font-medium">
                        {t('phone')}
                      </TableHead>
                      <TableHead className="text-muted-foreground font-medium">
                        {t('status')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((contact) => (
                      <TableRow
                        key={contact.resourceName}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedContacts.has(contact.resourceName)}
                            onCheckedChange={() =>
                              toggleContactSelection(contact.resourceName)
                            }
                            className="border-white/20"
                          />
                        </TableCell>
                        <TableCell className="text-popover-foreground">
                          {contact.firstName || contact.lastName ? (
                            `${contact.firstName} ${contact.lastName}`.trim()
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-popover-foreground">
                          {contact.email || (
                            <span className="text-muted-foreground italic">
                              No email
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-popover-foreground">
                          {contact.phone || (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className="bg-green-500/20 text-green-400 border-green-500/30 gap-1.5"
                          >
                            <CheckCircle2 className="size-3" />
                            {t('verified')}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-white/10 bg-muted/20">
          <Button
            variant="active"
            onClick={handleClose}
            disabled={isImporting}
            className="border-white/20 hover:bg-accent hover:text-accent-foreground"
          >
            {t('cancel')}
          </Button>
          {contacts.length > 0 && (
            <Button
              onClick={handleImport}
              disabled={selectedContacts.size === 0 || isImporting}
              variant="default"
              className="gap-2 font-medium"
            >
              {isImporting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('importing')}
                </>
              ) : (
                <>
                  <UserPlus className="size-4" />
                  {t('import_button', { count: selectedContacts.size })}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
